import { useCallback } from "react";
import { useI18nContext } from "@hopper-b2b/i18n";
import type { FareclassShelfBrandName } from "@hopper-b2b/types";
import { RadioDropdown } from "@hopper-b2b/ui";
import type { FareClassFilterSelectionProps } from "./container";
import "./styles.scss";

interface IFareClassFilterSelectionProps extends FareClassFilterSelectionProps {
  showDropdownContentOnly?: boolean;
}

interface IFareClassOption {
  label: string;
  value: FareclassShelfBrandName | "any";
}

export const FareClassFilterSelection = (
  props: IFareClassFilterSelectionProps
) => {
  const { fareClassFilter, setFareclassOptionFilter, showDropdownContentOnly } =
    props;

  const { t } = useI18nContext();

  // Determine currently selected fare class (or "any" if none/all selected)
  // Note: All false (initial state) and all true are functionally equivalent:
  // - All false = no filter applied = show all flights
  // - All true = match any class = show all flights
  // The RadioDropdown UI ensures only one class is selected at a time
  const selectedFareClass = (() => {
    const activeClasses = Object.entries(fareClassFilter).filter(
      ([_, isActive]) => isActive
    );
    const totalFareClasses = Object.keys(fareClassFilter).length;

    // If no classes selected or all selected, treat as "any"
    if (
      activeClasses.length === 0 ||
      activeClasses.length === totalFareClasses
    ) {
      return "any";
    }

    // Return the single active class
    return activeClasses[0][0] as FareclassShelfBrandName;
  })();

  const handleChange = useCallback(
    (value: string) => {
      if (value === "any") {
        // Set all fare classes to false (show all)
        setFareclassOptionFilter({
          basic: false,
          standard: false,
          enhanced: false,
          premium: false,
          luxury: false,
        });
      } else {
        // Set only the selected fare class to true
        const selectedClass = value as FareclassShelfBrandName;
        setFareclassOptionFilter({
          basic: selectedClass === "basic",
          standard: selectedClass === "standard",
          enhanced: selectedClass === "enhanced",
          premium: selectedClass === "premium",
          luxury: selectedClass === "luxury",
        });
      }
    },
    [setFareclassOptionFilter]
  );

  const fareClassOptions: IFareClassOption[] = [
    {
      label: t("fareClass.all"),
      value: "any",
    },
    {
      label: t("fareClass.basicLabel"),
      value: "basic",
    },
    {
      label: t("fareClass.standardLabel"),
      value: "standard",
    },
    {
      label: t("fareClass.enhancedLabel"),
      value: "enhanced",
    },
    {
      label: t("fareClass.premiumLabel"),
      value: "premium",
    },
    {
      label: t("fareClass.luxuryLabel"),
      value: "luxury",
    },
  ];

  return (
    <RadioDropdown
      dropdownLabel={t("searchFilter.fareClass")}
      options={fareClassOptions}
      setOption={handleChange}
      selected={selectedFareClass}
      showDropdownContentOnly={showDropdownContentOnly}
      buttonClassName="b2b-shop-filter"
      groupAriaLabel={t("searchFilter.filterFareClass")}
      buttonAriaLabel={t("searchFilter.changeFilterFareClass")}
      popoverClassName="b2b"
    />
  );
};
